import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getConceptsValuesSearch,
  type ConceptValue,
} from "../services/regulationsService";
function mapConceptSearchItemsToValues(
  items: Array<{ code: string; name: string; description?: string }>,
): ConceptValue[] {
  return items.map((item) => ({
    id: item.code,
    code: item.code,
    name: item.name ?? item.code,
    description: item.description ?? "",
  }));
}

function isConceptSearchResponseOk(
  response: Awaited<ReturnType<typeof getConceptsValuesSearch>>,
): boolean {
  return (
    response.status === "success" ||
    response.code === 200 ||
    response.code === 201 ||
    Array.isArray(response.data)
  );
}
export interface UsePaginatedConceptValuesResult {
  input: string;
  setInput: (value: string) => void;
  options: ConceptValue[];
  loading: boolean;
  hasMoreNext: boolean;
  loadingMore: "next" | "prev" | null;
  loadOptions: (searchName?: string) => Promise<void>;
  loadNext: () => Promise<void>;
  loadPrev: () => Promise<void>;
}

const DEFAULT_PAGE_SIZE = 20;

export type UsePaginatedConceptValuesOptions = {
  /**
   * When true and conceptCode is empty, search across all concepts
   * (omit concept_names on the user-search API if allConceptCodes is empty).
   */
  allowEmptyConceptAsAll?: boolean;
  /**
   * Concept codes to pass when conceptCode is empty ("All").
   */
  allConceptCodes?: string[];
};

/**
 * Loads concept values via user-search.
 * `conceptCode` is the Dimension/Concept dropdown value and is sent as
 * `concept_names` as-is (no display-label remapping).
 */
export function usePaginatedConceptValues(
  conceptCode: string,
  pageSize: number = DEFAULT_PAGE_SIZE,
  options?: UsePaginatedConceptValuesOptions,
): UsePaginatedConceptValuesResult {
  const allowEmptyConceptAsAll = options?.allowEmptyConceptAsAll === true;
  const allConceptCodesKey = options?.allConceptCodes?.join(",") ?? "";

  const conceptNames = useMemo(() => {
    const trimmed = conceptCode.trim();
    if (trimmed) return [trimmed];
    if (allConceptCodesKey) {
      return allConceptCodesKey
        .split(",")
        .map((code) => code.trim())
        .filter(Boolean);
    }
    return [];
  }, [conceptCode, allConceptCodesKey]);

  const canSearch =
    Boolean(conceptNames.length > 0) ||
    (allowEmptyConceptAsAll && !conceptCode.trim());

  const [input, setInputState] = useState("");
  const inputRef = useRef(input);
  inputRef.current = input;

  const setInput = useCallback((value: string) => {
    inputRef.current = value;
    setInputState(value);
  }, []);

  const [optionsState, setOptions] = useState<ConceptValue[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMoreNext, setHasMoreNext] = useState(true);
  const [loadingMore, setLoadingMore] = useState<"next" | "prev" | null>(null);
  const loadOptionsRequestIdRef = useRef(0);

  const conceptNamesKey = conceptNames.join(",");

  const loadOptions = useCallback(
    async (searchName?: string) => {
      if (!canSearch) {
        setOptions([]);
        setHasMoreNext(false);
        return;
      }

      const requestId = ++loadOptionsRequestIdRef.current;
      try {
        setLoading(true);
        setPage(1);
        setHasMoreNext(true);
        const trimmedSearch = searchName?.trim() || undefined;
        const response = await getConceptsValuesSearch(
          canSearch && conceptNames.length > 0 ? conceptNames : undefined,
          trimmedSearch,
          pageSize,
          1,
        );
        if (requestId !== loadOptionsRequestIdRef.current) return;
        if (isConceptSearchResponseOk(response)) {
          const data = mapConceptSearchItemsToValues(
            Array.isArray(response.data) ? response.data : [],
          );
          setOptions(data);
          setHasMoreNext(data.length >= pageSize);
        } else {
          setOptions([]);
          setHasMoreNext(false);
        }
      } catch (error) {
        if (requestId !== loadOptionsRequestIdRef.current) return;
        console.error(`Error loading concept values for ${conceptCode || "all"}:`, error);
        setOptions([]);
        setHasMoreNext(false);
      } finally {
        if (requestId === loadOptionsRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [canSearch, conceptCode, conceptNames, pageSize],
  );

  const loadNext = useCallback(async () => {
    if (!canSearch || !hasMoreNext || loadingMore) return;
    const nextPage = page + 1;
    const searchName = inputRef.current.trim() || undefined;
    try {
      setLoadingMore("next");
      const response = await getConceptsValuesSearch(
        conceptNames.length > 0 ? conceptNames : undefined,
        searchName,
        pageSize,
        nextPage,
      );
      if (isConceptSearchResponseOk(response)) {
        const newData = mapConceptSearchItemsToValues(
          Array.isArray(response.data) ? response.data : [],
        );
        setOptions((prev) => {
          const existingCodes = new Set(prev.map((item) => item.code));
          const appended = newData.filter((item) => !existingCodes.has(item.code));
          return appended.length > 0 ? [...prev, ...appended] : prev;
        });
        setPage(nextPage);
        setHasMoreNext(newData.length >= pageSize);
      } else {
        setHasMoreNext(false);
      }
    } catch {
      setHasMoreNext(false);
    } finally {
      setLoadingMore(null);
    }
  }, [canSearch, conceptNames, page, hasMoreNext, loadingMore, pageSize]);

  const loadPrev = useCallback(async () => {
    if (!canSearch || page <= 1 || loadingMore) return;
    const prevPage = page - 1;
    const searchName = inputRef.current.trim() || undefined;
    try {
      setLoadingMore("prev");
      const response = await getConceptsValuesSearch(
        conceptNames.length > 0 ? conceptNames : undefined,
        searchName,
        pageSize,
        prevPage,
      );
      if (isConceptSearchResponseOk(response)) {
        const newData = mapConceptSearchItemsToValues(
          Array.isArray(response.data) ? response.data : [],
        );
        setOptions((prev) => {
          const existingCodes = new Set(prev.map((item) => item.code));
          const toPrepend = newData.filter((item) => !existingCodes.has(item.code));
          return toPrepend.length > 0 ? [...toPrepend, ...prev] : prev;
        });
        setPage(prevPage);
      }
    } catch {
      // keep current page
    } finally {
      setLoadingMore(null);
    }
  }, [canSearch, conceptNames, page, loadingMore, pageSize]);

  useEffect(() => {
    setInput("");
    if (!canSearch) {
      setOptions([]);
      setPage(1);
      setHasMoreNext(false);
      return;
    }
    void loadOptions();
  }, [canSearch, conceptCode, conceptNamesKey, loadOptions, setInput]);

  return {
    input,
    setInput,
    options: optionsState,
    loading,
    hasMoreNext,
    loadingMore,
    loadOptions,
    loadNext,
    loadPrev,
  };
}
